import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDialog } from '@gokrew/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@gokrew/ui/components/table';
import { Plus, Mail, Phone, Building2 } from 'lucide-react';
import { getCrmContacts } from '@/api/crm';
import type { CrmPerson } from '@/api/crm';
import { CrmContactEditor } from './CrmContactEditor';
import { CrmButton, CrmEmpty, CrmError, CrmPagination } from './CrmShared';
import { useCrmFormat } from './crm-format';
import { crmRowInteraction } from './crm-row-interaction';
import styles from './Crm.module.css';
export const CrmContacts = ({ search }: { search: string }) => {
  const { t } = useCrmFormat();
  const [page, setPage] = useState(1);
  const { openDialog } = useDialog();
  const contacts = useQuery({
    queryKey: ['crm', 'contacts', search, page],
    queryFn: () => getCrmContacts({ search, page }),
  });
  const edit = (contact?: CrmPerson) =>
    openDialog({
      title: t(contact ? 'crm.workspace.edit_contact' : 'crm.add_contact'),
      containerClass: styles.editorDialog,
      content: <CrmContactEditor contact={contact} />,
    });
  return (
    <>
      <div className={styles.listMeta}>
        <span>
          {t('crm.workspace.contact_count', {
            count: contacts.data?.total ?? 0,
          })}
        </span>
      </div>
      {contacts.isPending ? (
        <p role="status" className={styles.loading}>
          {t('crm.loading')}
        </p>
      ) : contacts.isError ? (
        <CrmError retry={() => void contacts.refetch()} />
      ) : !contacts.data.items.length ? (
        <div className={styles.panel}>
          <CrmEmpty
            title={t(
              search ? 'crm.no_results' : 'crm.workspace.contacts_empty_title'
            )}
            description={t('crm.workspace.contacts_empty')}
            action={
              <CrmButton
                variant="adminPrimary"
                IconPrefix={Plus}
                onClick={() => edit()}
              >
                {t('crm.add_contact')}
              </CrmButton>
            }
          />
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <Table>
            <TableHeader>
              <TableRow>
                {[
                  'contact_name',
                  'contact_title',
                  'workspace.company',
                  'contact_email',
                  'contact_phone',
                ].map((key) => (
                  <TableHead key={key}>{t(`crm.${key}`)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.data.items.map((contact) => (
                <TableRow
                  key={contact.id}
                  className={styles.clickableRow}
                  aria-label={t('crm.edit_named', { name: contact.name })}
                  {...crmRowInteraction(() => edit(contact))}
                >
                  <TableCell>
                    <span className={styles.personName}>
                      <span className={styles.smallAvatar}>
                        {contact.name.slice(0, 1).toUpperCase()}
                      </span>
                      {contact.name}
                    </span>
                  </TableCell>
                  <TableCell>
                    {contact.title || (
                      <span className={styles.muted}>
                        {t('crm.workspace.not_added')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={styles.inlineIcon}>
                      <Building2 />
                      {contact.company?.name || t('crm.workspace.unassigned')}
                    </span>
                  </TableCell>
                  <TableCell>
                    {contact.email ? (
                      <a
                        className={styles.inlineIcon}
                        href={`mailto:${contact.email}`}
                      >
                        <Mail />
                        {contact.email}
                      </a>
                    ) : (
                      t('crm.workspace.not_added')
                    )}
                  </TableCell>
                  <TableCell>
                    {contact.phone ? (
                      <a
                        className={styles.inlineIcon}
                        href={`tel:${contact.phone}`}
                      >
                        <Phone />
                        {contact.phone}
                      </a>
                    ) : (
                      t('crm.workspace.not_added')
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {contacts.data && (
        <CrmPagination
          page={page}
          total={contacts.data.total}
          pageSize={contacts.data.pageSize}
          onPage={setPage}
        />
      )}
    </>
  );
};
